/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosNewIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import './ProductModal.scss';

// Animation variants
const backdropVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalVariants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: 30,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

const imageVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  }),
};

const thumbnailVariants = {
  initial: { opacity: 0, y: 10 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
    },
  }),
  hover: {
    y: -4,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
    },
  },
  tap: { scale: 0.95 },
};

function ProductModal({ product, onClose }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [[page, direction], setPage] = useState([0, 0]);
  const [touchStart, setTouchStart] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const modalRef = useRef(null);
  const imageContainerRef = useRef(null);
  const modalContentRef = useRef(null);

  // Check if device is mobile
  const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const goToNextImage = () => {
    const newIndex = (currentImageIndex + 1) % product.imageUrls.length;
    setPage([newIndex, 1]);
    setCurrentImageIndex(newIndex);
  };

  const goToPreviousImage = () => {
    const newIndex = (currentImageIndex - 1 + product.imageUrls.length) % product.imageUrls.length;
    setPage([newIndex, -1]);
    setCurrentImageIndex(newIndex);
  };

  const goToImage = (index) => {
    const newDirection = index > currentImageIndex ? 1 : -1;
    setPage([index, newDirection]);
    setCurrentImageIndex(index);
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const toggleFullscreen = () => {
    // For mobile devices, use the pseudo-fullscreen approach
    if (isMobile()) {
      setIsPseudoFullscreen(!isPseudoFullscreen);
      setIsFullscreen(!isFullscreen);
      return;
    }

    if (!isFullscreen) {
      // Enter fullscreen
      if (imageContainerRef.current.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
      }
      else if (imageContainerRef.current.webkitRequestFullscreen) { /* Safari */
        imageContainerRef.current.webkitRequestFullscreen();
      }
      else if (imageContainerRef.current.msRequestFullscreen) { /* IE11 */
        imageContainerRef.current.msRequestFullscreen();
      }
      // Fallback to pseudo-fullscreen if API is not supported
      else {
        setIsPseudoFullscreen(true);
      }
      setIsFullscreen(true);
    }
    else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen();
      }
      else if (document.msExitFullscreen) { /* IE11 */
        document.msExitFullscreen();
      }
      // Fallback for pseudo-fullscreen
      else if (isPseudoFullscreen) {
        setIsPseudoFullscreen(false);
      }
      setIsFullscreen(false);
    }
  };

  const handleClose = () => {
    // If in pseudo-fullscreen, exit it first
    if (isPseudoFullscreen) {
      setIsPseudoFullscreen(false);
      setIsFullscreen(false);
      return;
    }

    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
      else if (event.key === 'ArrowRight') goToNextImage();
      else if (event.key === 'ArrowLeft') goToPreviousImage();
      else if (event.key === 'z') toggleZoom();
      else if (event.key === 'f') toggleFullscreen();
    };

    window.addEventListener('keydown', handleKeyDown);

    // Lock body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Listen for fullscreen change events
    const fullscreenChangeHandler = () => {
      const isCurrentlyFullscreen = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement;

      // Only update if not in pseudo-fullscreen mode
      if (!isPseudoFullscreen) {
        setIsFullscreen(!!isCurrentlyFullscreen);
      }
    };

    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';

      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);
    };
  }, [onClose, isZoomed, isPseudoFullscreen]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;

    const touchEnd = e.changedTouches[0].clientX;
    if (touchStart - touchEnd > 75) goToNextImage();
    else if (touchStart - touchEnd < -75) goToPreviousImage();
    setTouchStart(null);
  };

  const handleClickOutside = (event) => {
    if (event.target === event.currentTarget) handleClose();
  };

  if (!product || !product.imageUrls) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={`modal ${isPseudoFullscreen ? 'pseudo-fullscreen' : ''}`}
        onClick={handleClickOutside}
        ref={modalRef}
        variants={backdropVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <motion.div
          className={`modal-content ${isPseudoFullscreen ? 'pseudo-fullscreen' : ''}`}
          onClick={(e) => e.stopPropagation()}
          ref={modalContentRef}
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        >
          <div
            className="close-button-wrapper"
            onClick={handleClose}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleClose();
            }}
          >
            <CloseIcon className="close-button" />
          </div>
          <div
            className={`image-container ${isZoomed ? 'zoomed' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchMove}
            ref={imageContainerRef}
          >
            <motion.div
              className="arrow arrow-left"
              onClick={goToPreviousImage}
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(201, 169, 98, 1)' }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowBackIosNewIcon />
            </motion.div>
            <div className="image-wrapper">
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.img
                  key={currentImageIndex}
                  src={product.imageUrls[currentImageIndex]}
                  alt={`${product.title} - ${currentImageIndex + 1}`}
                  className="modal-image"
                  onClick={toggleZoom}
                  custom={direction}
                  variants={imageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                />
              </AnimatePresence>
            </div>
            <motion.div
              className="arrow arrow-right"
              onClick={goToNextImage}
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(201, 169, 98, 1)' }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowForwardIosNewIcon />
            </motion.div>

            <div className="modal-controls">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isZoomed ? (
                  <ZoomOutIcon className="control-button" onClick={toggleZoom} title={t('productModal.zoomOut')} />
                ) : (
                  <ZoomInIcon className="control-button" onClick={toggleZoom} title={t('productModal.zoomIn')} />
                )}
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isFullscreen ? (
                  <FullscreenExitIcon className="control-button" onClick={toggleFullscreen} title={t('productModal.exitFullscreen')} />
                ) : (
                  <FullscreenIcon className="control-button" onClick={toggleFullscreen} title={t('productModal.enterFullscreen')} />
                )}
              </motion.div>
            </div>
          </div>

          <motion.div
            className="image-counter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {currentImageIndex + 1} / {product.imageUrls.length}
          </motion.div>

          <div className="thumbnails">
            {product.imageUrls.map((url, index) => (
              <motion.div
                key={index}
                className={`thumbnail-wrapper ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => goToImage(index)}
                variants={thumbnailVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                whileTap="tap"
                custom={index}
              >
                <img
                  src={url}
                  alt={`${product.title} thumbnail ${index + 1}`}
                  className="thumbnail"
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            className="product-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <h2 className="modal-title">{product.title}</h2>
            {product.price && (
              <div className="modal-price">{product.price}</div>
            )}
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ProductModal;
